import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import api, { apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatINR, formatDate } from "../../lib/format";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { InfoTip, GLOSSARY } from "../../components/app/InfoTip";
import GetTheApp from "../../components/app/GetTheApp";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { User, Wallet, Bell, Download, Trash2, LogOut, Lock, PlayCircle, ShieldCheck, Eye, EyeOff, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, pushSupported } from "../../lib/push";

const CATS = [
  { v: "upcoming", l: "Upcoming payments" }, { v: "due_today", l: "Due today" },
  { v: "overdue", l: "Overdue" }, { v: "renewal", l: "Annual renewals" },
  { v: "heavy", l: "Heavy commitment periods" }, { v: "safe_change", l: "Safe-to-Spend changes" },
];

function initials(name, email) {
  const n = (name || "").trim();
  if (n) return n.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (email || "?").slice(0, 2).toUpperCase();
}

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const { replayTour, openFeedback } = useOutletContext() || {};
  const [ip, setIp] = useState(null);
  const [rem, setRem] = useState({ timing: "3", in_app: true, push_enabled: false, categories: CATS.map((c) => c.v) });
  const [snap, setSnap] = useState(null);
  const [pd, setPd] = useState({ name: "", mobile: "", dob: "", country: "India", currency: "INR", timezone: "Asia/Kolkata", date_format: "DD/MM/YYYY" });
  const [pw, setPw] = useState({ password: "", confirm_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingIp, setSavingIp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [i, r, d] = await Promise.all([api.get("/income-profile"), api.get("/reminders"), api.get("/dashboard").catch(() => null)]);
      setIp(i.data || { monthly_income: "", salary_day: 1, income_type: "fixed", protected_reserve: "", available_balance: "" });
      if (r.data) setRem((prev) => ({ ...prev, ...r.data, categories: r.data.categories || prev.categories }));
      if (d?.data) setSnap(d.data.position);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user) setPd((p) => ({ ...p, name: user.name || "", mobile: user.mobile || "", dob: user.dob || "", country: user.country || "India", currency: user.currency || "INR", timezone: user.timezone || "Asia/Kolkata", date_format: user.date_format || "DD/MM/YYYY" }));
  }, [user]);

  const upd = (k) => (v) => setIp((p) => ({ ...p, [k]: v }));

  const saveIp = async () => {
    setSavingIp(true);
    try {
      await api.put("/income-profile", {
        monthly_income: parseFloat(ip.monthly_income) || 0, salary_day: parseInt(ip.salary_day) || 1,
        income_type: ip.income_type || "fixed", additional_income: parseFloat(ip.additional_income) || 0,
        available_balance: ip.available_balance === "" || ip.available_balance == null ? null : parseFloat(ip.available_balance),
        protected_reserve: parseFloat(ip.protected_reserve) || 0,
      });
      toast.success("Financial profile updated.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingIp(false); }
  };

  const savePd = async () => {
    try { await api.patch("/profile", pd); await (refresh && refresh()); toast.success("Profile updated."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveRem = async (next) => {
    setRem(next);
    try { await api.put("/reminders", next); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const togglePush = async (on) => {
    if (on) {
      if (!pushSupported()) { toast.error("Your browser doesn't support push notifications."); return; }
      const res = await enablePush();
      if (res.ok) { setRem((r) => ({ ...r, push_enabled: true })); toast.success("Notifications enabled."); try { await api.post("/push/test"); } catch (e) {} }
      else if (res.reason === "denied") toast.error("Notifications are blocked in your browser settings. Enable them there, then try again.");
      else toast.error("Couldn't enable notifications.");
    } else {
      await disablePush(); setRem((r) => ({ ...r, push_enabled: false })); toast.success("Notifications turned off.");
    }
  };

  const toggleCat = (v) => {
    const set = new Set(rem.categories || []);
    set.has(v) ? set.delete(v) : set.add(v);
    saveRem({ ...rem, categories: [...set] });
  };

  const changePw = async () => {
    if (pw.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw.password !== pw.confirm_password) { toast.error("Passwords do not match."); return; }
    try { await api.post("/auth/set-password", pw); setPw({ password: "", confirm_password: "" }); toast.success("Password updated."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/auth/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "commitmentt-export.json"; a.click();
      URL.revokeObjectURL(url); toast.success("Your data has been exported.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteAccount = async () => {
    try { await api.delete("/auth/account"); await logout(); nav("/"); toast.success("Account deleted."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="space-y-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>;

  // Profile completeness: only meaningful config counts
  const checks = [
    (parseFloat(ip.monthly_income) || 0) > 0,
    (parseFloat(ip.protected_reserve) || 0) > 0,
    !!(user?.mobile || pd.mobile),
    !!rem.timing,
    !!rem.push_enabled,
  ];
  const completeness = Math.round((checks.filter(Boolean).length / checks.length) * 100);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1"><User size={22} className="text-navy" strokeWidth={1.5} /><h1 className="font-heading text-2xl font-medium text-ink">Profile</h1></div>

      {/* Header */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5 flex items-center gap-4" data-testid="profile-header">
        <div className="h-16 w-16 rounded-2xl bg-navy text-white flex items-center justify-center font-heading text-xl shrink-0" data-testid="profile-avatar">{initials(user?.name, user?.email)}</div>
        <div className="min-w-0">
          <p className="font-heading text-lg text-ink truncate">{user?.name || "Your name"}</p>
          <p className="text-sm text-muted-foreground truncate">{user?.email || user?.phone}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Member since {formatDate(user?.created_at)}</p>
        </div>
      </Card>

      {/* Snapshot */}
      {snap && (
        <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="profile-snapshot">
          <p className="text-sm font-medium text-ink mb-3">Your COMMITMENTT snapshot</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { l: "Monthly income", v: formatINR(snap.monthly_income) },
              { l: "Active commitments", v: snap.active_count },
              { l: "Monthly committed", v: formatINR(snap.monthly_recurring) },
              { l: "Commitment ratio", v: `${snap.commitment_ratio}%`, info: GLOSSARY.commitment_ratio },
              { l: "Protected reserve", v: formatINR(snap.protected_reserve), info: GLOSSARY.protected_reserve },
              { l: "Safe to spend", v: formatINR(snap.safe_to_spend), info: GLOSSARY.safe_to_spend, accent: true },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-secondary/60 p-3">
                <p className="text-[11px] text-slatey flex items-center gap-1">{s.l}{s.info && <InfoTip {...s.info} className="h-4 w-4" />}</p>
                <p className={`font-heading text-lg tnum mt-0.5 ${s.accent ? "text-mint" : "text-ink"}`}>{s.v}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Personal details */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="profile-personal">
        <p className="text-sm font-medium text-ink mb-4">Personal details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="text-sm text-ink">Full name</Label><Input value={pd.name} onChange={(e) => setPd({ ...pd, name: e.target.value })} className="mt-1 rounded-xl" data-testid="pd-name" /></div>
          <div><Label className="text-sm text-ink">Email</Label><Input value={user?.email || "—"} disabled className="mt-1 rounded-xl bg-secondary/50" data-testid="pd-email" /></div>
          <div><Label className="text-sm text-ink">Mobile</Label><Input value={pd.mobile} onChange={(e) => setPd({ ...pd, mobile: e.target.value })} className="mt-1 rounded-xl" data-testid="pd-mobile" /></div>
          <div><Label className="text-sm text-ink">Date of birth</Label><Input type="date" value={pd.dob} onChange={(e) => setPd({ ...pd, dob: e.target.value })} className="mt-1 rounded-xl" data-testid="pd-dob" /></div>
          <div><Label className="text-sm text-ink">Country</Label><Input value={pd.country} onChange={(e) => setPd({ ...pd, country: e.target.value })} className="mt-1 rounded-xl" data-testid="pd-country" /></div>
          <div><Label className="text-sm text-ink">Currency</Label>
            <Select value={pd.currency} onValueChange={(v) => setPd({ ...pd, currency: v })}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="pd-currency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="INR">₹ INR</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem><SelectItem value="GBP">£ GBP</SelectItem></SelectContent>
            </Select></div>
          <div><Label className="text-sm text-ink">Date format</Label>
            <Select value={pd.date_format} onValueChange={(v) => setPd({ ...pd, date_format: v })}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="pd-dateformat"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem><SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem><SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem></SelectContent>
            </Select></div>
        </div>
        <Button onClick={savePd} className="rounded-full mt-4 bg-navy text-white hover:bg-navy-light" data-testid="pd-save">Save details</Button>
      </Card>

      {/* Financial profile */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5">
        <div className="flex items-center gap-2 mb-4"><Wallet size={18} className="text-navy" /><p className="text-sm font-medium text-ink">Financial profile</p></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label className="text-sm text-ink">Monthly income (₹)</Label><Input type="number" value={ip.monthly_income} onChange={(e) => upd("monthly_income")(e.target.value)} className="mt-1 rounded-xl" data-testid="profile-income" /></div>
          <div><Label className="text-sm text-ink">Salary day</Label><Input type="number" min={1} max={28} value={ip.salary_day} onChange={(e) => upd("salary_day")(e.target.value)} className="mt-1 rounded-xl" data-testid="profile-salaryday" /></div>
          <div><Label className="text-sm text-ink">Additional income (₹)</Label><Input type="number" value={ip.additional_income ?? ""} onChange={(e) => upd("additional_income")(e.target.value)} className="mt-1 rounded-xl" data-testid="profile-addincome" /></div>
          <div><Label className="text-sm text-ink flex items-center gap-1">Protected reserve (₹) <InfoTip {...GLOSSARY.protected_reserve} className="h-4 w-4" /></Label><Input type="number" value={ip.protected_reserve} onChange={(e) => upd("protected_reserve")(e.target.value)} className="mt-1 rounded-xl" data-testid="profile-reserve" /></div>
        </div>
        <Button onClick={saveIp} disabled={savingIp} className="rounded-full mt-4 bg-navy text-white hover:bg-navy-light" data-testid="profile-save-income">{savingIp ? "Saving…" : "Save"}</Button>
      </Card>

      {/* Notifications */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="notif-settings">
        <div className="flex items-center gap-2 mb-3"><Bell size={18} className="text-navy" /><p className="text-sm font-medium text-ink">Notifications</p></div>
        <div className="rounded-xl bg-mint-light/50 border border-mint/20 p-4">
          <p className="text-sm font-medium text-ink">Stay ahead of your commitments</p>
          <p className="text-xs text-slatey mt-1">COMMITMENTT can remind you before important payments and alert you when upcoming commitments may affect your available money.</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-ink">Push notifications</span>
            <Switch checked={!!rem.push_enabled} onCheckedChange={togglePush} data-testid="push-toggle" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4 mb-2">Remind me before due dates</p>
        <div className="flex flex-wrap gap-2">
          {[{ v: "1", l: "1 day before" }, { v: "3", l: "3 days before" }, { v: "7", l: "7 days before" }].map((o) => (
            <Button key={o.v} size="sm" onClick={() => saveRem({ ...rem, timing: o.v })}
              className={`rounded-full text-xs h-9 ${rem.timing === o.v ? "bg-navy text-white" : "bg-secondary text-slatey hover:bg-border"}`}
              data-testid={`reminder-${o.v}`}>{o.l}</Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4 mb-2">What to notify me about</p>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => {
            const on = (rem.categories || []).includes(c.v);
            return <button key={c.v} onClick={() => toggleCat(c.v)} data-testid={`notif-cat-${c.v}`}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${on ? "bg-mint-light text-mint font-medium" : "bg-secondary text-slatey"}`}>{c.l}</button>;
          })}
        </div>
      </Card>

      {/* Preferences */}
      <GetTheApp />
      <Card className="rounded-2xl border-border shadow-card-sm p-5 space-y-1" data-testid="prefs">
        <p className="text-sm font-medium text-ink mb-2">Preferences</p>
        <button onClick={() => replayTour && replayTour()} className="w-full flex items-center gap-3 py-2.5 text-left text-ink hover:bg-secondary rounded-xl px-2" data-testid="replay-tour">
          <PlayCircle size={18} className="text-navy" /><span className="text-sm">Replay product tour</span>
        </button>
        <button onClick={() => openFeedback && openFeedback()} className="w-full flex items-center gap-3 py-2.5 text-left text-ink hover:bg-secondary rounded-xl px-2" data-testid="give-feedback">
          <MessageSquarePlus size={18} className="text-navy" /><span className="text-sm">Give feedback</span>
        </button>
        <p className="text-xs text-muted-foreground px-2 pt-1">COMMITMENTT is in Early Access — tell us what's confusing, useful or missing.</p>
      </Card>

      {/* Learn COMMITMENTT hub */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="learn-hub">
        <p className="text-sm font-medium text-ink mb-1">Learn COMMITMENTT</p>
        <p className="text-xs text-muted-foreground mb-3">Short, plain-language explainers.</p>
        <div className="divide-y divide-border/60">
          {[
            ["What is a commitment?", "Any recurring payment you've promised — an EMI, rent, subscription, insurance or bill. COMMITMENTT keeps them in one place."],
            ["Understanding Safe to Spend", "An estimate of what's available after the commitments due this cycle and the reserve you chose to protect. It's a guide, not a guarantee or financial advice."],
            ["Understanding Commitment Ratio", "The percentage of your income represented by your recorded recurring commitments. Lower generally means more breathing room."],
            ["Understanding Protected Reserve", "Money you'd prefer COMMITMENTT not to treat as available to spend, so it isn't counted in your Safe to Spend."],
            ["Using the Calendar", "Your commitments become a timeline — see what's due today, this week and later, and mark payments as paid."],
            ["Uploading statements", "Upload a PDF and we'll try to extract the details. Statements vary, so always review what we found before saving."],
            ["Managing notifications", "Turn on reminders to get a heads-up before payments are due. We only ask for permission when you choose to enable it."],
          ].map(([q, a]) => (
            <details key={q} className="group py-2.5" data-testid="learn-item">
              <summary className="cursor-pointer text-sm text-ink list-none flex items-center justify-between">{q}<span className="text-muted-foreground group-open:rotate-180 transition-transform">⌄</span></summary>
              <p className="text-xs text-slatey mt-1.5 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </Card>

      {/* Security */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="security">
        <div className="flex items-center gap-2 mb-3"><Lock size={18} className="text-navy" /><p className="text-sm font-medium text-ink">Security</p></div>
        <div className="space-y-3">
          <div>
            <Label className="text-sm text-ink">New password</Label>
            <div className="relative mt-1">
              <Input type={showPw ? "text" : "password"} value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} className="rounded-xl pr-10" data-testid="sec-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="sec-toggle-pw">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div><Label className="text-sm text-ink">Confirm password</Label><Input type={showPw ? "text" : "password"} value={pw.confirm_password} onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })} className="mt-1 rounded-xl" data-testid="sec-confirm" /></div>
          <Button onClick={changePw} className="rounded-full bg-navy text-white hover:bg-navy-light" data-testid="sec-change">Change password</Button>
        </div>
        <Button variant="secondary" onClick={async () => { await logout(); nav("/"); }} className="w-full rounded-full bg-secondary text-ink justify-start mt-4" data-testid="profile-logout"><LogOut size={16} className="mr-2" />Sign out</Button>
      </Card>

      {/* Profile completeness */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5" data-testid="completeness">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-ink flex items-center gap-1.5"><ShieldCheck size={16} className="text-mint" />Profile completeness</p>
          <span className="text-sm font-medium text-navy tnum">{completeness}%</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-mint transition-all" style={{ width: `${completeness}%` }} /></div>
        <p className="text-xs text-muted-foreground mt-2">We only count settings that make COMMITMENTT more useful — income, reserve, mobile, reminders and notifications.</p>
      </Card>

      {/* Data & privacy */}
      <Card className="rounded-2xl border-border shadow-card-sm p-5 space-y-3" data-testid="data-privacy">
        <p className="text-sm font-medium text-ink">Your data</p>
        <Button variant="secondary" onClick={exportData} className="w-full rounded-full bg-secondary text-ink justify-start" data-testid="export-btn"><Download size={16} className="mr-2" />Download my data</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" className="w-full rounded-full text-calm-alert justify-start hover:bg-calm-alertbg" data-testid="delete-account-btn"><Trash2 size={16} className="mr-2" />Delete my account</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your COMMITMENTT account?</AlertDialogTitle>
              <AlertDialogDescription>This will permanently delete your account and associated COMMITMENTT data. This action cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} className="rounded-full bg-calm-alert hover:bg-calm-alert/90" data-testid="delete-account-confirm">Delete everything</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
}
